import React from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

interface FeatureDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  type: "feature" | "permission";
  feature: PartnerFeatureEnum | null;
}

export const FeatureDialog = ({
  isOpen,
  onOpenChange,
  type,
  feature,
}: FeatureDialogProps) => {
  const { t } = useTranslation();
  const { hasOrgFeature } = useOrgPermissions();

  const getFeatureLabel = (featureEnum: PartnerFeatureEnum | null): string => {
    if (!featureEnum) return "";

    switch (featureEnum) {
      case PartnerFeatureEnum.BulkMessaging:
        return t("global.features.bulkMessaging");
      case PartnerFeatureEnum.Games:
        return t("global.features.automatedGames");
      case PartnerFeatureEnum.Rise:
        return t("global.features.RISE");
      case PartnerFeatureEnum.RisePredictiveModels:
        return t("global.features.RisePredictiveModels");
      default:
        return String(featureEnum).toLowerCase();
    }
  };

  const hasRISE = hasOrgFeature(PartnerFeatureEnum.Rise);
  const hasRISEModels = hasOrgFeature(PartnerFeatureEnum.RisePredictiveModels);
  const waitingOnModels =
    (feature === PartnerFeatureEnum.RisePredictiveModels ||
      feature === PartnerFeatureEnum.Rise) &&
    hasRISE &&
    !hasRISEModels;

  const featureLabel = getFeatureLabel(feature);

  const getTitle = (): string => {
    if (waitingOnModels) return t("upgrade.features.modelsTitle");
    return type === "feature"
      ? t("upgrade.features.title", {
          feature: featureLabel,
        })
      : t("upgrade.permission.title");
  };

  const getMessage = (): React.ReactNode => {
    if (waitingOnModels) return t("upgrade.features.modelsMessage");
    return type === "feature" ? (
      <Trans
        i18nKey="upgrade.features.message"
        values={{ feature: featureLabel }}
        components={{
          supportLink: (
            <a
              href="mailto:support@zeemee.com"
              className="text-blue-primary hover:underline"
            />
          ),
        }}
      />
    ) : (
      <Trans
        i18nKey="upgrade.permission.message"
        components={{
          supportLink: (
            <a
              href="mailto:support@zeemee.com"
              className="text-blue-primary hover:underline"
            />
          ),
        }}
      />
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex min-h-[323px] flex-col rounded-lg border p-6">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription className="py-4">{getMessage()}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-auto">
          <DialogClose asChild>
            <Button>{t("global.close")}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
